// @flow

import * as React from 'react';
import trackEventAJAX, { trackClick } from './tracking';
import type { TrackingEventAJAXProps } from './tracking';

type Props = {
  /** The clickable children */
  children: React.Node,

  /** If we should only fire tracking once and ignore subsequent clicks. Defaults to true. */
  once?: boolean,

  /** Render with a span instead of a div when you don't want a block-level element. */
  useSpan?: boolean,

  /** Force tracking request to go through AJAX rather than cookie.
   * Use case example: tripadvisor.com/careers is not an actual servlet. */
  forceAjax?: boolean,
} & TrackingEventAJAXProps;

type State = {|
  /** if a descendant component has been clicked yet */
  clicked: boolean,
|};

/**
 * Tracks clicks to its' children by either firing an AJAX call or setting an event cookie. It will look at the element
 * clicked and walk its' ancestry up to the ClickTracker root and if it finds an anchor with an href to the same domain
 * then it will set an event cookie. Otherwise it will fire an AJAX request.
 *
 * Note that a link to external site will result in a race condition since the event cookie mechanism will not work on
 * external sites (since we cannot pick the cookie up) and there is no guarantee the AJAX will be sent prior to the
 * page unloading.
 */
class ClickTracker extends React.Component<Props, State> {
  static defaultProps = {
    once: true,
  };

  state = {
    clicked: false,
  };

  click = (event: SyntheticEvent<*>) => {
    if (this.props.once && this.state.clicked) {
      return;
    }

    if (!this.props.forceAjax) {
      trackClick(event, this.props);
    } else {
      // otherwise we fall back to using an AJAX call.
      trackEventAJAX(this.props);
    }

    // update the state to 'clicked'
    this.setState({ clicked: true });
  };

  render() {
    const trackable = !this.props.once || !this.state.clicked;
    const elemType = this.props.useSpan ? 'span' : 'div';
    return React.createElement(
      elemType,
      {
        onClick: trackable ? this.click : undefined,
      },
      this.props.children,
    );
  }
}

export default ClickTracker;
